<template>
  <div class="platform-auth-container">
    <div class="auth-card">
      <div class="auth-header">
        <div class="logo">
          <img src="@/assets/omind_logo.png" alt="OMIND" class="logo-img" />
        </div>
        <h1>Platform Admin</h1>
        <p class="subtitle">Multi-Tenant Administration Console</p>
      </div>

      <template v-if="!otpStep">
        <div class="auth-tabs">
          <button
            type="button"
            :class="['tab', { active: activeTab === 'login' }]"
            @click="activeTab = 'login'"
          >
            Login
          </button>
          <button
            type="button"
            :class="['tab', { active: activeTab === 'register' }]"
            @click="activeTab = 'register'"
          >
            Register
          </button>
        </div>

        <!-- Login Form -->
        <form v-if="activeTab === 'login'" class="auth-form" @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="email">Email</label>
            <input
              id="email"
              v-model="loginForm.email"
              type="email"
              data-testid="platform-login-email"
              placeholder="admin@omind.com"
              required
              :disabled="loading"
            />
          </div>

          <div class="form-group">
            <label for="password">Password</label>
            <input
              id="password"
              v-model="loginForm.password"
              type="password"
              data-testid="platform-login-password"
              placeholder="••••••••"
              required
              :disabled="loading"
            />
          </div>

          <div v-if="error" class="error-message">
            {{ error }}
          </div>

          <div v-if="success" class="success-message">
            {{ success }}
          </div>

          <button
            type="submit"
            class="submit-btn"
            data-testid="platform-login-submit"
            :disabled="loading"
          >
            <span v-if="loading">Logging in...</span>
            <span v-else>Login</span>
          </button>

          <div class="auth-links">
            <router-link
              to="/platform/forgot-password"
              class="auth-link"
              data-testid="platform-forgot-link"
            >
              Forgot password?
            </router-link>
            <button
              type="button"
              class="auth-link-btn"
              data-testid="platform-resend-verification"
              :disabled="loading || !loginForm.email"
              @click="handleResendVerification"
            >
              Resend verification
            </button>
          </div>
        </form>

        <!-- Register Form -->
        <form v-if="activeTab === 'register'" class="auth-form" @submit.prevent="handleRegister">
          <div class="form-group">
            <label for="reg-name">Name</label>
            <input
              id="reg-name"
              v-model="registerForm.name"
              type="text"
              placeholder="Your full name"
              required
              :disabled="loading"
            />
          </div>

          <div class="form-group">
            <label for="reg-email">Email</label>
            <input
              id="reg-email"
              v-model="registerForm.email"
              type="email"
              :placeholder="`you@${adminDomain}`"
              required
              :disabled="loading"
            />
            <small class="hint">Must use @{{ adminDomain }} domain</small>
          </div>

          <div class="form-group">
            <label for="reg-password">Password</label>
            <input
              id="reg-password"
              v-model="registerForm.password"
              type="password"
              placeholder="Minimum 8 characters"
              required
              minlength="8"
              :disabled="loading"
            />
          </div>

          <div class="form-group">
            <label for="reg-confirm">Confirm Password</label>
            <input
              id="reg-confirm"
              v-model="registerForm.confirmPassword"
              type="password"
              placeholder="Confirm password"
              required
              :disabled="loading"
            />
          </div>

          <div v-if="error" class="error-message">
            {{ error }}
          </div>

          <div v-if="success" class="success-message">
            {{ success }}
          </div>

          <button type="submit" class="submit-btn" :disabled="loading">
            <span v-if="loading">Registering...</span>
            <span v-else>Register</span>
          </button>
        </form>
      </template>

      <!-- OTP Step (same card) -->
      <form v-else class="auth-form" @submit.prevent="handleVerifyOtp">
        <p class="otp-hint">
          Enter the verification code sent to <strong>{{ otpEmail }}</strong>
        </p>

        <div v-if="success" class="success-message">
          {{ success }}
        </div>

        <div class="form-group">
          <label for="otp">Verification code</label>
          <input
            id="otp"
            v-model="otpCode"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            data-testid="platform-otp-input"
            placeholder="6-digit code"
            required
            :disabled="loading"
          />
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <button
          type="submit"
          class="submit-btn"
          data-testid="platform-otp-submit"
          :disabled="loading || otpCode.length !== 6"
        >
          <span v-if="loading">Verifying...</span>
          <span v-else>Verify</span>
        </button>

        <button type="button" class="auth-link-btn back-link" :disabled="loading" @click="backToLogin">
          Back to login
        </button>
      </form>

      <div class="auth-footer">
        <p>Platform administration for multi-tenant environments</p>
      </div>
    </div>
  </div>
</template>

<script>
import { platformApi } from '@/services/platformApi';

export default {
  name: 'PlatformLogin',
  data() {
    return {
      activeTab: 'login',
      loading: false,
      error: null,
      success: null,
      adminDomain: 'omind.com',
      otpStep: false,
      otpEmail: '',
      otpCode: '',
      loginForm: {
        email: '',
        password: '',
      },
      registerForm: {
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
      },
    };
  },
  methods: {
    storeTokensAndRedirect(data) {
      localStorage.setItem('platform_token', data.token);
      localStorage.setItem('platform_refresh_token', data.refresh_token);
      localStorage.setItem('platform_user', JSON.stringify(data.user));
      this.$router.push('/platform');
    },

    async handleLogin() {
      this.error = null;
      this.success = null;
      this.loading = true;

      try {
        const data = await platformApi.login(this.loginForm.email, this.loginForm.password);

        if (data.requiresOtp) {
          this.otpStep = true;
          this.otpEmail = data.email || this.loginForm.email;
          this.otpCode = '';
          this.success = data.message || 'A verification code has been sent to your email.';
          return;
        }

        if (data.success && data.token) {
          this.storeTokensAndRedirect(data);
        }
      } catch (err) {
        this.error = err.response?.data?.message || 'Login failed. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async handleVerifyOtp() {
      this.error = null;
      this.loading = true;

      try {
        const data = await platformApi.verifyOtp(this.otpEmail, this.otpCode);
        if (data.success && data.token) {
          this.storeTokensAndRedirect(data);
        } else {
          this.error = data.message || 'OTP verification failed. Please try again.';
        }
      } catch (err) {
        this.error = err.response?.data?.message || 'Invalid or expired code. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    backToLogin() {
      this.otpStep = false;
      this.otpCode = '';
      this.error = null;
      this.success = null;
    },

    async handleResendVerification() {
      this.error = null;
      this.success = null;
      this.loading = true;

      try {
        const data = await platformApi.resendVerification(this.loginForm.email);
        this.success = data.message || 'If the account exists, a verification email has been sent.';
      } catch (err) {
        this.error = err.response?.data?.message || 'Could not resend verification email.';
      } finally {
        this.loading = false;
      }
    },

    async handleRegister() {
      this.error = null;
      this.success = null;
      this.loading = true;

      if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.error = 'Passwords do not match';
        this.loading = false;
        return;
      }

      try {
        const data = await platformApi.register({
          name: this.registerForm.name,
          email: this.registerForm.email,
          password: this.registerForm.password,
        });

        if (data.success) {
          this.success = data.message || 'Registration successful! Please check your email to verify your account.';
          this.activeTab = 'login';
          this.loginForm.email = this.registerForm.email;
          this.registerForm = {
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
          };
        }
      } catch (err) {
        this.error = err.response?.data?.message || 'Registration failed. Please try again.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.platform-auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f8f8 0%, #ffffff 50%, #f8f8f8 100%);
  padding: 20px;
  font-family: 'AsenPro', sans-serif;
}

.auth-card {
  background: #ffffff;
  border: 1px solid #e7e7e7;
  border-radius: 2rem;
  padding: 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
}

.auth-header {
  text-align: center;
  margin-bottom: 30px;
}

.logo {
  margin-bottom: 16px;
}

.logo-img {
  width: 160px;
  height: auto;
}

.auth-header h1 {
  color: #1E2019;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.subtitle {
  color: #666666;
  font-size: 14px;
  margin: 8px 0 0;
}

.auth-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
}

.tab {
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: #f4f4f4;
  color: #666666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 2rem;
  transition: all 0.2s;
}

.tab:hover {
  background: rgba(228, 130, 26, 0.1);
  color: #e4821a;
}

.tab.active {
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  color: #fff;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: #1E2019;
  font-size: 13px;
  font-weight: 500;
}

.form-group input {
  padding: 14px 16px;
  border: 1px solid #e7e7e7;
  border-radius: 0.75rem;
  background: #f8f8f8;
  color: #1E2019;
  font-size: 15px;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #e4821a;
  background: #ffffff;
}

.form-group input::placeholder {
  color: #999999;
}

.form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hint {
  color: #999999;
  font-size: 12px;
}

.otp-hint {
  color: #666666;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.error-message {
  background: rgba(228, 75, 85, 0.1);
  border: 1px solid rgba(228, 75, 85, 0.3);
  color: #e44b55;
  padding: 12px 16px;
  border-radius: 0.75rem;
  font-size: 13px;
}

.success-message {
  background: rgba(174, 234, 183, 0.2);
  border: 1px solid rgba(174, 234, 183, 0.5);
  color: #2d8a3e;
  padding: 12px 16px;
  border-radius: 0.75rem;
  font-size: 13px;
}

.submit-btn {
  padding: 14px 24px;
  border: none;
  border-radius: 2rem;
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(228, 130, 26, 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.auth-link,
.auth-link-btn {
  color: #e4821a;
  font-size: 13px;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
}

.auth-link:hover,
.auth-link-btn:hover:not(:disabled) {
  text-decoration: underline;
}

.auth-link-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.back-link {
  align-self: center;
}

.auth-footer {
  margin-top: 30px;
  text-align: center;
}

.auth-footer p {
  color: #999999;
  font-size: 12px;
  margin: 0;
}
</style>
